import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isWorkspacePath, parseAppLocation, buildAppPath } from './sections';
import { useAuth } from '../../hooks/useAuth';
import { useApi } from '../../hooks/useApi';
import { useLightbox } from '../../hooks/useLightbox';
import { useUrlParams, readSearchParams } from '../../hooks/useUrlParams';
import { normalizeId } from '../../utils/formatters';
import { getUserLabel } from '../../utils/users';
import { getCabinetRole, getWorkspaceRole, getOwnershipScopeLabel } from '../../utils/workspaces';
import { getPublicSlug, createWorkspaceMap, getWorkspaceById as getWorkspaceByIdBase, getAssignedCabinetMap, getVisibleWorkspaces, getUserCabinets, getAvailableProjectCabinets } from '../../utils/selectors';
import { loadCurrentUser, loadMarketplaces, loadCompanyUsers, loadProfileSummary, loadWorkspaces, loadCompany, updateCompanyMode } from '../../services/company';
import { handleProfileSave as handleProfileSaveBase, handleAvatarUpload as handleAvatarUploadBase, handleCertificatesUpload as handleCertificatesUploadBase, handleTelegramLink as handleTelegramLinkBase, loadProfileStats as loadProfileStatsBase } from '../../services/profile';
import { openEditAccount as openEditAccountBase, closeEditAccount as closeEditAccountBase, handleEditSubmit as handleEditSubmitBase } from '../../services/marketplace-edit';
import { loadBillingSummary } from '../../services/billing';
import logger from "../../utils/logger";
const LAST_PROJECT_KEY = 'anylex_current_project';
export const initialMarketplace = {
  marketplace_type: 'ozon',
  account_name: '',
  api_key: '',
  seller_id: '',
  client_id: '',
  client_secret: ''
};
export function useAppState() {
  const {
    token,
    refreshToken,
    user,
    setUser,
    logout
  } = useAuth();
  const {
    request,
    authHeader,
    status,
    setStatus,
    showStatus,
    API_BASE_URL
  } = useApi();
  const {
    lightbox,
    openLightbox,
    closeLightbox,
    goLightbox
  } = useLightbox();
  const location = useLocation();
  const navigate = useNavigate();
  const initialAppLocation = useMemo(
    () => parseAppLocation(location.pathname, location.search),
    []
  );
  const [activeSection, setActiveSection] = useState(initialAppLocation.section);
  const [activeMarketplace, setActiveMarketplace] = useState(initialAppLocation.marketplaceType);
  // Текущий проект — параметр адреса `project` (единственный источник: F5, ссылка и «Назад» его не теряют).
  // Открытая карточка проекта в разделе «Проекты» — часть пути /app/projects/<id>.
  const { get: getUrlParam, update: updateUrlParams } = useUrlParams();
  const activeProjectId = getUrlParam('project');
  const openProjectId = useMemo(
    () => parseAppLocation(location.pathname, location.search).openProjectId,
    [location.pathname, location.search]
  );
  const setActiveProjectId = useCallback(
    (projectId, options) => updateUrlParams({ project: projectId || '' }, { push: Boolean(options?.push) }),
    [updateUrlParams]
  );
  const openProjectCard = useCallback((projectId) => {
    const next = readSearchParams();
    next.set('project', projectId);
    navigate({ pathname: `/app/projects/${projectId}`, search: `?${next.toString()}` });
  }, [navigate]);
  const closeProjectCard = useCallback(() => {
    navigate({ pathname: '/app/projects', search: window.location.search });
  }, [navigate]);
  // Переход в инструменты маркетплейса выбранного проекта одним адресом, без цепочки эффектов.
  const openMarketplaceTools = useCallback((marketplaceType, projectId) => {
    const next = new URLSearchParams();
    if (projectId) next.set('project', projectId);
    if (marketplaceType === 'ozon') next.set('tool', 'analytics');
    navigate({ pathname: `/app/marketplace/${marketplaceType}`, search: `?${next.toString()}` });
  }, [navigate]);
  const [marketplaces, setMarketplaces] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [companyInfoBusy, setCompanyInfoBusy] = useState(false);
  const [crmDeals, setCrmDeals] = useState([]);
  const [profileSummary, setProfileSummary] = useState(null);
  const [profileSummaryBusy, setProfileSummaryBusy] = useState(false);
  const [profileStats, setProfileStats] = useState(null);
  const [profileStatsBusy, setProfileStatsBusy] = useState(false);
  const [profileBusy, setProfileBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [certificatesBusy, setCertificatesBusy] = useState(false);
  const [telegramBusy, setTelegramBusy] = useState(false);
  const [editAccount, setEditAccount] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [projectEditId, setProjectEditId] = useState(null);
  const [projectForm, setProjectForm] = useState(null);
  const [savedProjectFilter, setSavedProjectFilter] = useState('');
  const [settingsTab, setSettingsTab] = useState(initialAppLocation.tab || 'cabinets');
  const [busy, setBusy] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [billingSummary, setBillingSummary] = useState(null);
  const [orderDynamics, setOrderDynamics] = useState(null);
  const [orderDynamicsError, setOrderDynamicsError] = useState(false);
  const [userPlanFeatures, setUserPlanFeatures] = useState(null);

  // Shared latch: whichever effect (state->URL or URL->state) applies a
  // change first records it here, so the other one sees it's already in
  // sync and skips — this is what keeps browser back/forward from being
  // fought by the state-driven navigate below.
  const lastSyncedPathRef = useRef(`${location.pathname}${location.search}`);
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  // Первая синхронизация после входа заменяет адрес (иначе «Назад» вернул бы на страницу-редирект),
  // а дальнейшие переходы между разделами пишутся в историю — работает кнопка «Назад» браузера.
  const firstUrlSyncRef = useRef(true);

  // state -> URL (user switched section/marketplace/settings tab in the UI)
  useEffect(() => {
    if (!token || !isWorkspacePath(pathnameRef.current)) return;
    const isFirstSync = firstUrlSyncRef.current;
    firstUrlSyncRef.current = false;
    // Ozon's own project/account/tool query params are owned by useOzonState, not
    // this hook — pass them through unchanged so this effect doesn't strip them
    // out on unrelated section/marketplace/settingsTab changes (including on the
    // very first run after a page reload with a deep link in the URL). They're
    // only applied when still on marketplace/ozon; buildAppPath drops them for
    // any other section/marketplace automatically.
    const currentParams = readSearchParams();
    const marketplaceParams = {
      project: currentParams.get('project') || '',
      account: currentParams.get('account') || '',
      tool: currentParams.get('tool') || '',
      sub: currentParams.get('sub') || '',
      from: currentParams.get('from') || '',
      to: currentParams.get('to') || '',
      camp: currentParams.get('camp') || '',
      tf: currentParams.get('tf') || '',
      view: currentParams.get('view') || '',
    };
    const currentLocation = parseAppLocation(window.location.pathname, window.location.search);
    const keepOpenProject = activeSection === 'projects' && currentLocation.section === 'projects' ? currentLocation.openProjectId : '';
    const targetPath = buildAppPath(activeSection, activeMarketplace, settingsTab, marketplaceParams, keepOpenProject);
    if (targetPath === lastSyncedPathRef.current) return;
    lastSyncedPathRef.current = targetPath;
    // Новая запись в истории — только при смене раздела. После «Назад» путь уже тот же (меняются лишь
    // параметры), и дополнительная запись превратила бы «Назад» в хождение по кругу.
    const samePath = targetPath.split('?')[0] === pathnameRef.current;
    navigate(targetPath, { replace: isFirstSync || samePath });
  }, [token, activeSection, activeMarketplace, settingsTab]);

  // URL -> state (direct link, page reload, or browser back/forward)
  useEffect(() => {
    if (!token || !isWorkspacePath(location.pathname)) return;
    const currentPath = `${location.pathname}${location.search}`;
    if (currentPath === lastSyncedPathRef.current) return;
    lastSyncedPathRef.current = currentPath;
    const parsed = parseAppLocation(location.pathname, location.search);
    setActiveSection(parsed.section);
    setActiveMarketplace(parsed.marketplaceType);
    if (parsed.tab) setSettingsTab(parsed.tab);
  }, [token, location.pathname, location.search]);

  const companyId = companyInfo?.id || user?.company_id || user?.companyId || '';
  const companyMode = companyInfo?.company_mode || 'holding';
  const moduleFlags = user?.module_flags || {};
  const crmEnabled = !['disabled'].includes(moduleFlags['crm'] || 'enabled');
  const isAdmin = user?.role === 'admin';
  const publicSlug = useMemo(() => getPublicSlug(), []);
  const logoCyrSrc = `${process.env.PUBLIC_URL}/brand/anylex-logo-cyr.png`;
  const workspaceMap = useMemo(() => createWorkspaceMap(workspaces, normalizeId), [workspaces]);
  const getWorkspaceById = workspaceId => getWorkspaceByIdBase(workspaceMap, normalizeId, workspaceId);
  const transferAllowedRoles = useMemo(() => new Set(['admin', 'admin_api']), []);
  const getCabinetRoleLocal = accountId => getCabinetRole(accountId, marketplaces, normalizeId);
  const getWorkspaceRoleLocal = workspace => getWorkspaceRole(workspace, user?.id, normalizeId);
  const getOwnershipScopeLabelLocal = transfer => getOwnershipScopeLabel(transfer, getWorkspaceById, marketplaces);
  const visibleWorkspaces = useMemo(() => getVisibleWorkspaces(workspaces, user?.id, normalizeId, getWorkspaceRoleLocal), [workspaces, user?.id, getWorkspaceRoleLocal]);
  const assignedCabinetMap = useMemo(() => getAssignedCabinetMap(workspaces), [workspaces]);
  const userCabinets = useMemo(() => getUserCabinets(marketplaces, user, normalizeId), [marketplaces, user]);
  const availableProjectCabinets = useMemo(() => getAvailableProjectCabinets(marketplaces, assignedCabinetMap, projectEditId), [marketplaces, assignedCabinetMap, projectEditId]);
  const loadCurrentUserLocal = async () => loadCurrentUser(request, setUser);
  const loadMarketplacesLocal = async companyIdValue => loadMarketplaces(request, companyIdValue, setMarketplaces);
  const loadCompanyUsersLocal = async () => loadCompanyUsers(request, companyId, setCompanyUsers);
  const loadCompanyInfoLocal = async () => {
    if (!companyId) return;
    try {
      await loadCompany(request, setCompanyInfo);
    } catch (error) {
      logger.error(error);
    }
  };
  const handleAgencyToggle = async () => {
    if (!companyId || companyInfoBusy) return;
    const nextMode = companyMode === 'agency' ? 'holding' : 'agency';
    setCompanyInfoBusy(true);
    try {
      const updated = await updateCompanyMode(request, nextMode);
      setCompanyInfo(updated);
      if (nextMode === 'agency' && activeSection !== 'crm') {
        setActiveSection('crm');
      }
      if (nextMode !== 'agency' && activeSection === 'crm') {
        setActiveSection('projects');
      }
      showStatus('success', nextMode === 'agency' ? 'Режим агентства включён.' : 'Режим агентства выключен.');
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Не удалось обновить режим компании.');
    } finally {
      setCompanyInfoBusy(false);
    }
  };
  const loadProfileSummaryLocal = async () => loadProfileSummary(request, user?.id, setProfileSummary, setProfileSummaryBusy);
  const loadWorkspacesLocal = async () => loadWorkspaces(request, companyId, setWorkspaces);
  const loadCrmDealsLocal = async () => {
    if (!crmEnabled) {
      setCrmDeals([]);
      return;
    }
    const data = await request('/api/v1/crm/deals');
    setCrmDeals(data);
  };
  const handleLogout = async () => {
    if (refreshToken) {
      try {
        await request('/api/v1/auth/logout', {
          method: 'POST',
          body: JSON.stringify({
            refresh_token: refreshToken
          })
        });
      } catch (error) {
        logger.error(error);
      }
    }
    logout();
    setMarketplaces([]);
    showStatus('info', 'Сессия завершена.');
  };
  const handleProfileSave = payload => handleProfileSaveBase({
    payload,
    user,
    request,
    setProfileBusy,
    setStatus,
    setUser,
    showStatus,
    loadProfileSummary: loadProfileSummaryLocal,
    loadProfileStats
  });
  const loadProfileStats = () => loadProfileStatsBase(request, user?.id, setProfileStats, setProfileStatsBusy);
  const handleAvatarUpload = file => handleAvatarUploadBase({
    file,
    user,
    apiBaseUrl: API_BASE_URL,
    authHeader,
    setAvatarBusy,
    setUser,
    showStatus
  });
  const handleCertificatesUpload = files => handleCertificatesUploadBase({
    files,
    user,
    apiBaseUrl: API_BASE_URL,
    authHeader,
    setCertificatesBusy,
    setUser,
    showStatus
  });
  const handleTelegramLink = () => handleTelegramLinkBase({
    user,
    request,
    setTelegramBusy,
    showStatus
  });
  const openEditAccount = account => openEditAccountBase(account, setEditAccount, setEditForm);
  const closeEditAccount = () => closeEditAccountBase(setEditAccount, setEditForm);
  const handleEditSubmit = event => handleEditSubmitBase({
    event,
    editAccount,
    editForm,
    request,
    setEditBusy,
    setStatus,
    showStatus,
    loadMarketplaces: loadMarketplacesLocal,
    closeEditAccount,
    companyId
  });
  useEffect(() => {
    if (!token) return;
    setWorkspaces([]);
    setMarketplaces([]);
    setCompanyUsers([]);
    setCompanyInfo(null);
    setBillingSummary(null);
    setOrderDynamics(null);
    setOrderDynamicsError(false);
    loadCurrentUserLocal().catch(() => {});
  }, [token]);
  useEffect(() => {
    if (!token) {
      setMarketplaces([]);
      setWorkspaces([]);
      setCompanyUsers([]);
      setCompanyInfo(null);
      setBillingSummary(null);
      setOrderDynamics(null);
      setOrderDynamicsError(false);
      return;
    }
    // Load marketplaces without requiring companyId (it's only for billing)
    loadMarketplacesLocal(companyId || null).catch(() => {});
    loadWorkspacesLocal().catch(() => {});
    if (companyId) {
      loadCompanyUsersLocal().catch(() => {});
      loadCompanyInfoLocal().catch(() => {});
      loadBillingSummary(request, setBillingSummary).catch(() => {});
      setOrderDynamicsError(false);
      request('/api/v1/billing/order-dynamics').then(data => {
        setOrderDynamics(data);
      }).catch(() => {
        setOrderDynamics(null);
        setOrderDynamicsError(true);
      });
      request('/api/v1/billing/my-features').then(data => {
        setUserPlanFeatures(data.features || {});
      }).catch(() => setUserPlanFeatures({}));
    }
  }, [companyId, token]);
  useEffect(() => {
    if (!user?.id) return;
    loadProfileSummaryLocal().catch(() => {});
  }, [user?.id]);
  // Нет проекта в адресе или он недоступен: берём последний выбранный, иначе первый.
  useEffect(() => {
    if (!token || !workspaces.length) return;
    if (activeProjectId && workspaces.some(item => item.id === activeProjectId)) {
      try { localStorage.setItem(LAST_PROJECT_KEY, activeProjectId); } catch { /* хранилище недоступно */ }
      return;
    }
    let saved = '';
    try { saved = localStorage.getItem(LAST_PROJECT_KEY) || ''; } catch { /* хранилище недоступно */ }
    const pick = workspaces.find(item => item.id === saved) || workspaces[0];
    if (pick) setActiveProjectId(pick.id);
  }, [token, workspaces, activeProjectId]);
  useEffect(() => {
    if (user?.id) {
      loadProfileStats();
    }
  }, [user?.id]);
  useEffect(() => {
    if (!token) return;
    let lastRefresh = Date.now();
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastRefresh > 30_000) {
        lastRefresh = Date.now();
        loadCurrentUserLocal().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [token]);
  return {
    token,
    user,
    setUser,
    request,
    authHeader,
    status,
    setStatus,
    showStatus,
    API_BASE_URL,
    lightbox,
    openLightbox,
    closeLightbox,
    goLightbox,
    activeSection,
    setActiveSection,
    activeMarketplace,
    setActiveMarketplace,
    activeProjectId,
    openProjectId,
    openProjectCard,
    closeProjectCard,
    openMarketplaceTools,
    setActiveProjectId,
    marketplaces,
    setMarketplaces,
    workspaces,
    setWorkspaces,
    companyUsers,
    companyInfo,
    setCompanyInfo,
    companyInfoBusy,
    crmDeals,
    setCrmDeals,
    profileSummary,
    profileSummaryBusy,
    profileStats,
    profileStatsBusy,
    profileBusy,
    avatarBusy,
    certificatesBusy,
    telegramBusy,
    editAccount,
    editForm,
    editBusy,
    projectModalOpen,
    projectEditId,
    projectForm,
    savedProjectFilter,
    setSavedProjectFilter,
    setProjectModalOpen,
    setProjectEditId,
    setProjectForm,
    settingsTab,
    setSettingsTab,
    busy,
    setBusy,
    logoError,
    setLogoError,
    billingSummary,
    orderDynamics,
    orderDynamicsError,
    userPlanFeatures,
    companyId,
    companyMode,
    crmEnabled,
    isAdmin,
    publicSlug,
    logoCyrSrc,
    getWorkspaceById,
    transferAllowedRoles,
    getCabinetRoleLocal,
    getWorkspaceRoleLocal,
    getOwnershipScopeLabelLocal,
    visibleWorkspaces,
    assignedCabinetMap,
    userCabinets,
    availableProjectCabinets,
    loadMarketplacesLocal,
    loadWorkspacesLocal,
    loadCompanyUsersLocal,
    loadCompanyInfoLocal,
    loadProfileSummaryLocal,
    loadCrmDealsLocal,
    handleLogout,
    handleProfileSave,
    loadProfileStats,
    handleAvatarUpload,
    handleCertificatesUpload,
    handleTelegramLink,
    handleAgencyToggle,
    openEditAccount,
    closeEditAccount,
    handleEditSubmit,
    getUserLabel,
    loadCurrentUserLocal,
  };
}
